"use client";

import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentArrowUpIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

const API = "https://erp-xe9u.onrender.com/api";

const formatCurrency = (num) =>
  Number.isFinite(num)
    ? `$${num.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "$0.00";

const isSFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return (
    u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
  );
};

const isLFUnit = (uom) => {
  const u = (uom || "").toLowerCase();
  return u === "lf" || u.includes("linear") || u.includes("lin ft");
};

const parseSizeToCoverage = (sizeStr, uom) => {
  if (!sizeStr) return null;
  const str = String(sizeStr).trim().toLowerCase();
  const parseLength = (part) => {
    if (!part) return 0;
    const mixedMatch = part.match(/^(\d+(?:\.\d+)?)' ?(\d+(?:\.\d+)?)"?$/);
    if (mixedMatch)
      return (
        (parseFloat(mixedMatch[1]) || 0) + (parseFloat(mixedMatch[2]) || 0) / 12
      );
    const feetMatch = part.match(/^([\d.]+)'?$/);
    if (feetMatch) return parseFloat(feetMatch[1]) || 0;
    const inchMatch = part.match(/^([\d.]+)"$/);
    if (inchMatch) return (parseFloat(inchMatch[1]) || 0) / 12;
    const num = parseFloat(part);
    return isNaN(num) ? 0 : num;
  };
  if (isSFUnit(uom)) {
    const parts = str.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const w = parseLength(parts[0]),
        h = parseLength(parts[1]);
      if (w > 0 && h > 0) return w * h;
    }
    const area = parseLength(parts[0]);
    return area > 0 ? area : null;
  }
  if (isLFUnit(uom)) {
    const length = parseLength(str);
    return length > 0 ? length : null;
  }
  return null;
};

/**
 * Extract assembly key by stripping leading number + dash.
 * "2 - 1st - MS.18"  → "1st - ms.18"
 * "58 - 1st - MS.18" → "1st - ms.18"
 * "22 - BFLCA - MS.F2" → "bflca - ms.f2"
 */
const extractAssemblyKey = (conditionName) => {
  if (!conditionName) return "";
  const str = conditionName.trim();
  const match = str.match(/^\d+\s*-\s*(.+)$/);
  return match ? match[1].trim().toLowerCase() : str.toLowerCase();
};

/**
 * Normalize size to feet for comparison.
 * "11'6\" ( 11'6\" )" → 11.5
 * "10'" → 10
 * "188\"" → 15.67
 */
const normalizeSizeToFeet = (sizeStr) => {
  if (!sizeStr) return 0;
  let str = String(sizeStr).trim();
  // Remove parenthetical duplicates: "11'6\" ( 11'6\" )" → "11'6\""
  const parenMatch = str.match(/^(.+?)\s*\(.*\)\s*$/);
  if (parenMatch) str = parenMatch[1].trim();

  const feetInchMatch = str.match(/^(\d+)'(\d+)"?$/);
  if (feetInchMatch)
    return (
      (parseFloat(feetInchMatch[1]) || 0) +
      (parseFloat(feetInchMatch[2]) || 0) / 12
    );

  const feetMatch = str.match(/^(\d+(?:\.\d+)?)'$/);
  if (feetMatch) return parseFloat(feetMatch[1]) || 0;

  const inchMatch = str.match(/^(\d+(?:\.\d+)?)"$/);
  if (inchMatch) return (parseFloat(inchMatch[1]) || 0) / 12;

  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

/**
 * Clean size string to canonical display form.
 * "11'6\" ( 11'6\" )" → "11'6\""
 */
const cleanSizeString = (sizeStr) => {
  if (!sizeStr) return "";
  let str = String(sizeStr).trim();
  const parenMatch = str.match(/^(.+?)\s*\(.*\)\s*$/);
  if (parenMatch) str = parenMatch[1].trim();
  return str;
};

export default function EditChangeOrderExcelUploadModal({
  open,
  onClose,
  onAddItems,
  databaseItems: externalDatabaseItems = [],
  vendors = [],
  loadingDatabase = false,
  existingChangeItems = [],
  defaultMarkupPercentage = 30,
  budgetItems = [],
  netQuantities = new Map(),
  netLoading = false,
  projectId = null,
}) {
  const [file, setFile] = useState(null);
  const [parsedItems, setParsedItems] = useState([]);
  const [validationResults, setValidationResults] = useState({
    budgetMatched: [],
    databaseMatched: [],
    invalid: [],
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedBudgetItems, setSelectedBudgetItems] = useState(new Set());
  const [selectedDbItems, setSelectedDbItems] = useState(new Set());
  const fileInputRef = useRef(null);

  // Internal database items (fetch if not provided externally)
  const [internalDatabaseItems, setInternalDatabaseItems] = useState([]);
  const [loadingInternalDb, setLoadingInternalDb] = useState(false);

  const databaseItems =
    externalDatabaseItems.length > 0
      ? externalDatabaseItems
      : internalDatabaseItems;
  const isLoadingDb = loadingDatabase || loadingInternalDb;

  useEffect(() => {
    const fetchDatabaseItems = async () => {
      if (!open) return;
      if (externalDatabaseItems.length > 0) return;
      if (internalDatabaseItems.length > 0) return;
      try {
        setLoadingInternalDb(true);
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${API}/item-database?limit=5000&isActive=true`,
          { headers },
        );
        setInternalDatabaseItems(res.data.items || []);
      } catch (err) {
        console.error("[ExcelUpload] Failed to fetch database items:", err);
      } finally {
        setLoadingInternalDb(false);
      }
    };
    fetchDatabaseItems();
  }, [open, externalDatabaseItems.length, internalDatabaseItems.length]);

  // ─── Assembly key → budget items lookup ───
  // Groups all budget line items by their normalized assembly key
  const assemblyKeyMap = useMemo(() => {
    const map = new Map();
    budgetItems.forEach((item) => {
      if (!item.conditionName) return;
      const key = extractAssemblyKey(item.conditionName);
      if (!map.has(key)) {
        map.set(key, { conditionName: item.conditionName, items: [] });
      }
      map.get(key).items.push(item);
    });
    return map;
  }, [budgetItems]);

  // ─── Item code → database item lookup ───
  const itemCodeMap = useMemo(() => {
    const map = new Map();
    databaseItems.forEach((item) => {
      if (item.itemCode) map.set(item.itemCode.toLowerCase().trim(), item);
    });
    return map;
  }, [databaseItems]);

  const findDatabaseItem = (code) => {
    if (!code) return null;
    const searchCode = code.toLowerCase().trim();
    let found = itemCodeMap.get(searchCode);
    if (found) return found;
    const cleanCode = searchCode.replace(/[^a-z0-9]/gi, "");
    for (const [key, item] of itemCodeMap.entries()) {
      if (key.replace(/[^a-z0-9]/gi, "") === cleanCode) return item;
    }
    return null;
  };

  const resolveNetKey = (item) => {
    if (!item) return "";
    if (item.isNewSpec) {
      return (item.lineItemId || item._id || item.key)?.toString() || "";
    }
    return (
      (
        item.key ||
        item.originalLineItemId ||
        item.lineItemId ||
        item._id
      )?.toString() || ""
    );
  };

  const findVendorByName = (searchName) => {
    if (!searchName) return null;
    const search = searchName.toLowerCase().trim();
    return (
      vendors.find((v) => v.companyName?.toLowerCase().trim() === search) ||
      vendors.find((v) => v.companyName?.toLowerCase().includes(search)) ||
      vendors.find((v) =>
        search.includes(v.companyName?.toLowerCase().trim()),
      ) ||
      null
    );
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setParsedItems([]);
      setValidationResults({
        budgetMatched: [],
        databaseMatched: [],
        invalid: [],
      });
      setStep(1);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (
      droppedFile &&
      (droppedFile.name.endsWith(".xlsx") || droppedFile.name.endsWith(".xls"))
    ) {
      setFile(droppedFile);
      setParsedItems([]);
      setValidationResults({
        budgetMatched: [],
        databaseMatched: [],
        invalid: [],
      });
      setStep(1);
    }
  }, []);

  const handleDragOver = (e) => e.preventDefault();

  // ─── Parse Excel File ───
  const parseExcelFile = async () => {
    if (!file) return;
    setIsProcessing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      const mappedItems = jsonData.map((row, index) => {
        const category = (row["Category"] || "M").toUpperCase().trim();
        const laborCode = row["Labor Code"] || "";
        const matCode =
          row["Mat. Code"] || row["Material Code"] || row["Mat Code"] || "";
        let finalItemCode = (category === "M" ? matCode : laborCode)
          .replace(/\s*\([^)]*\)\s*/g, "")
          .trim();

        // Parse coverage from explicit column first
        let coverage = 1;
        const coverageStr = row["Coverage/Unit"];
        if (coverageStr && coverageStr !== "-") {
          const parsed = parseFloat(coverageStr);
          if (!isNaN(parsed) && parsed > 0) coverage = parsed;
        }

        const rawSize = row["Size"] || "";
        const cleanedSize = cleanSizeString(rawSize);
        const uom = (row["UoM"] || "EA").toUpperCase().trim();

        // Fallback: compute coverage from size if not provided
        if (coverage === 1 && cleanedSize && (isSFUnit(uom) || isLFUnit(uom))) {
          const sizeCoverage = parseSizeToCoverage(cleanedSize, uom);
          if (sizeCoverage && sizeCoverage > 0) coverage = sizeCoverage;
        }

        // For materials use Req Qty (pieces), for labor use Quantity (coverage total)
        const quantityValue =
          category === "M"
            ? parseFloat(row["Req Qty"]) || 0
            : parseFloat(row["Quantity"]) || 0;

        return {
          _rowIndex: index + 2,
          itemNumber: row["Item #"] || index + 1,
          laborId: row["Labor ID"] === "-" ? null : row["Labor ID"] || null,
          category,
          conditionName: row["Condition"] || "",
          quantity: quantityValue, // signed — positive or negative
          uom,
          unitCost: parseFloat(row["Unit Cost"]) || 0,
          coveragePerUnit: coverage,
          size: cleanedSize,
          rawSize,
          sectionNumber: row["Section"] || "",
          itemCode: finalItemCode,
          matCode: matCode.trim(),
          laborCode: laborCode.trim(),
          bidArea: row["Area"] || "",
          hours: parseFloat(row["Hours"]) || 0,
          vendor: row["Vendor"] || "",
        };
      });

      setParsedItems(mappedItems);
      validateItems(mappedItems);
      setStep(2);
    } catch (error) {
      console.error("Error parsing Excel:", error);
      alert("Error parsing Excel file: " + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // ─── Validate: Budget Match → Database Fallback ───
  const validateItems = (items) => {
    const budgetMatched = [];
    const databaseMatched = [];
    const invalid = [];

    // Build duplicate detection set
    const existingKeys = new Set(
      existingChangeItems.map(
        (i) =>
          `${(i.itemCode || "").toLowerCase()}-${(i.conditionName || "").toLowerCase()}-${(i.bidArea || "").toLowerCase()}-${(i.size || "").toLowerCase()}`,
      ),
    );

    items.forEach((excelRow) => {
      const errors = [];
      const warnings = [];

      // ── Step 1: Try to match assembly in budget ──
      const excelAssemblyKey = extractAssemblyKey(excelRow.conditionName);
      const assemblyMatch = assemblyKeyMap.get(excelAssemblyKey);

      if (assemblyMatch) {
        const excelSizeInFeet = normalizeSizeToFeet(excelRow.size);
        const excelItemCode = (excelRow.itemCode || "").toLowerCase().trim();
        const excelBidArea = (excelRow.bidArea || "").toLowerCase().trim();

        // Filter assembly items by itemCode match
        const candidates = assemblyMatch.items.filter((budgetItem) => {
          const budgetCode = (
            budgetItem.itemCode ||
            budgetItem.materialCode ||
            budgetItem.laborCode ||
            ""
          )
            .toLowerCase()
            .trim();
          return budgetCode === excelItemCode;
        });

        // Among candidates, find best match by size + bidArea
        let bestMatch = null;
        let bestSizeDiff = Infinity;

        candidates.forEach((candidate) => {
          const budgetSizeInFeet = normalizeSizeToFeet(
            candidate.latestSize || candidate.size || "",
          );
          const sizeDiff = Math.abs(excelSizeInFeet - budgetSizeInFeet);
          const budgetBidArea = (candidate.bidArea || "").toLowerCase().trim();
          const bidAreaMatches =
            !excelBidArea || !budgetBidArea || budgetBidArea === excelBidArea;

          if (bidAreaMatches && sizeDiff < bestSizeDiff) {
            bestSizeDiff = sizeDiff;
            bestMatch = candidate;
          }
        });

        // Fallback: if no bid area match, take best size match
        if (!bestMatch && candidates.length > 0) {
          bestMatch = candidates[0];
          if (bestSizeDiff > 1) {
            warnings.push(
              `Size mismatch: Excel="${excelRow.size}", Budget="${bestMatch.latestSize || bestMatch.size || "N/A"}"`,
            );
          }
        }

        if (bestMatch) {
          if (
            bestSizeDiff > 0.5 &&
            bestMatch === candidates[0] &&
            candidates.length > 1
          ) {
            warnings.push(
              `Size mismatch: Excel="${excelRow.size}", Budget="${bestMatch.latestSize || bestMatch.size || "N/A"}"`,
            );
          }

          // ── Validate negative quantities against COMBINED net remaining across ALL candidates ──
          const delta = excelRow.quantity;
          const itemKey = (
            bestMatch.originalLineItemId ||
            bestMatch.lineItemId ||
            bestMatch._id ||
            bestMatch.key
          )?.toString();

          const netInfo = netQuantities.get(itemKey);
          const singleNetRemaining =
            netInfo?.netRemaining ??
            netInfo?.remainingQty ??
            bestMatch.remainingQty ??
            bestMatch.requiredQty ??
            0;

          if (delta < 0) {
            let totalAvailable = 0;
            candidates.forEach((candidate) => {
              const candKey = (
                candidate.originalLineItemId ||
                candidate.lineItemId ||
                candidate._id ||
                candidate.key
              )?.toString();
              const candNetInfo = netQuantities.get(candKey);
              const candRemaining =
                candNetInfo?.netRemaining ??
                candNetInfo?.remainingQty ??
                candidate.netRemaining ??
                candidate.remainingQty ??
                0;
              totalAvailable += Math.max(0, candRemaining);
            });

            assemblyKeyMap.forEach((otherAssembly, otherKey) => {
              if (otherKey === excelAssemblyKey) return;
              otherAssembly.items.forEach((otherItem) => {
                const otherCode = (
                  otherItem.itemCode ||
                  otherItem.materialCode ||
                  otherItem.laborCode ||
                  ""
                )
                  .toLowerCase()
                  .trim();
                if (otherCode !== excelItemCode) return;
                const otherBidArea = (otherItem.bidArea || "")
                  .toLowerCase()
                  .trim();
                if (
                  excelBidArea &&
                  otherBidArea &&
                  otherBidArea !== excelBidArea
                )
                  return;

                const otherKey2 = (
                  otherItem.originalLineItemId ||
                  otherItem.lineItemId ||
                  otherItem._id ||
                  otherItem.key
                )?.toString();
                const otherNetInfo = netQuantities.get(otherKey2);
                const otherRemaining =
                  otherNetInfo?.netRemaining ??
                  otherNetInfo?.remainingQty ??
                  otherItem.netRemaining ??
                  otherItem.remainingQty ??
                  0;
                totalAvailable += Math.max(0, otherRemaining);
              });
            });

            if (Math.abs(delta) > totalAvailable) {
              errors.push(
                `Cannot deduct ${Math.abs(delta)} pcs — only ${totalAvailable.toFixed(2)} total net remaining across ${candidates.length} matching item(s). Max deduction: -${totalAvailable.toFixed(2)}`,
              );
            }
          }

          // Check duplicates
          const dupKey = `${excelItemCode}-${assemblyMatch.conditionName.toLowerCase()}-${excelBidArea}-${(excelRow.size || "").toLowerCase()}`;
          if (existingKeys.has(dupKey)) {
            errors.push("Duplicate: already exists in the change order");
          }

          const enrichedItem = {
            ...excelRow,
            _matchType: "budget",
            _budgetItem: bestMatch,
            _assemblyConditionName: assemblyMatch.conditionName,
            _netRemaining: singleNetRemaining,
            _sourceLineItemId: itemKey,
            _errors: errors,
            _warnings: warnings,
            _isValid: errors.length === 0,
            itemDescription:
              bestMatch.itemDescription ||
              bestMatch.costCodeDescription ||
              excelRow.itemCode,
          };

          if (enrichedItem._isValid) {
            budgetMatched.push(enrichedItem);
          } else {
            invalid.push(enrichedItem);
          }
          return;
        }

        // No exact item within assembly — fall through to database
        warnings.push(
          `Assembly "${assemblyMatch.conditionName}" found but no matching item "${excelRow.itemCode}" within it`,
        );
      }

      // ── Step 2: Fallback — match against item database ──
      const dbItem = findDatabaseItem(excelRow.itemCode);

      if (!dbItem) {
        errors.push(
          `"${excelRow.itemCode}" not found in budget assemblies or item database. Please add this item to the database first.`,
        );
        invalid.push({
          ...excelRow,
          _matchType: "none",
          _errors: [...warnings, ...errors],
          _warnings: [],
          _isValid: false,
        });
        return;
      }

      if (dbItem.category !== excelRow.category) {
        errors.push(
          `Category mismatch: Excel="${excelRow.category}" vs Database="${dbItem.category}"`,
        );
      }

      if (!excelRow.conditionName) {
        errors.push("Missing Condition/Assembly name");
      }

      // Negative quantities not allowed for non-budget (database) items
      if (excelRow.quantity < 0 && !assemblyMatch) {
        errors.push(
          "Negative quantities only allowed for items matched to budget assemblies",
        );
      }

      const vendorMatch = findVendorByName(excelRow.vendor);

      const dupKey = `${(excelRow.itemCode || "").toLowerCase()}-${(excelRow.conditionName || "").toLowerCase()}-${(excelRow.bidArea || "").toLowerCase()}-${(excelRow.size || "").toLowerCase()}`;
      if (existingKeys.has(dupKey)) {
        errors.push("Duplicate: already exists in the change order");
      }

      const enrichedItem = {
        ...excelRow,
        _matchType: "database",
        _dbItem: dbItem,
        _vendorMatch: vendorMatch,
        _notInDatabase: false,
        _errors: errors,
        _warnings: warnings,
        _isValid: errors.length === 0,
        itemDescription: dbItem.description || excelRow.itemCode,
      };

      if (enrichedItem._isValid) {
        databaseMatched.push(enrichedItem);
      } else {
        invalid.push(enrichedItem);
      }
    });

    setValidationResults({ budgetMatched, databaseMatched, invalid });
    setSelectedBudgetItems(new Set(budgetMatched.map((_, i) => i)));
    setSelectedDbItems(new Set(databaseMatched.map((_, i) => i)));
  };

  // ─── Build final items to add ───
  // ─── Build final items to add ───
  const handleAddSelectedItems = () => {
    const itemsToAdd = [];
    let idx = 0;

    // ── Budget-matched items ──
    validationResults.budgetMatched
      .filter((_, i) => selectedBudgetItems.has(i))
      .forEach((item) => {
        const budgetItem = item._budgetItem;
        const isMaterial = item.category === "M";
        const signedDelta = item.quantity; // already signed from Excel

        // ── For NEGATIVE deltas, split across all matching budget line items ──
        if (signedDelta < 0) {
          const excelItemCode = (item.itemCode || "").toLowerCase().trim();
          const excelBidArea = (item.bidArea || "").toLowerCase().trim();
          const excelAssemblyKey = extractAssemblyKey(item.conditionName);
          const assemblyMatch = assemblyKeyMap.get(excelAssemblyKey);

          // Collect all candidate budget items with their available quantities
          const candidatesWithRemaining = [];

          // 1) Candidates from the matched assembly
          if (assemblyMatch) {
            assemblyMatch.items.forEach((candidate) => {
              const candidateCode = (
                candidate.itemCode ||
                candidate.materialCode ||
                candidate.laborCode ||
                ""
              )
                .toLowerCase()
                .trim();
              if (candidateCode !== excelItemCode) return;

              const candidateBidArea = (candidate.bidArea || "")
                .toLowerCase()
                .trim();
              if (
                excelBidArea &&
                candidateBidArea &&
                candidateBidArea !== excelBidArea
              )
                return;

              const candKey = (
                candidate.originalLineItemId ||
                candidate.lineItemId ||
                candidate._id ||
                candidate.key
              )?.toString();
              const candNetInfo = netQuantities.get(candKey);
              const candRemaining = Math.max(
                0,
                candNetInfo?.netRemaining ??
                  candNetInfo?.remainingQty ??
                  candidate.netRemaining ??
                  candidate.remainingQty ??
                  0,
              );

              if (candRemaining > 0) {
                candidatesWithRemaining.push({
                  budgetItem: candidate,
                  key: candKey,
                  netInfo: candNetInfo,
                  remaining: candRemaining,
                });
              }
            });
          }

          // 2) Candidates from OTHER assemblies with same item code + bid area
          assemblyKeyMap.forEach((otherAssembly, otherKey) => {
            if (otherKey === excelAssemblyKey) return;
            otherAssembly.items.forEach((otherItem) => {
              const otherCode = (
                otherItem.itemCode ||
                otherItem.materialCode ||
                otherItem.laborCode ||
                ""
              )
                .toLowerCase()
                .trim();
              if (otherCode !== excelItemCode) return;

              const otherBidArea = (otherItem.bidArea || "")
                .toLowerCase()
                .trim();
              if (excelBidArea && otherBidArea && otherBidArea !== excelBidArea)
                return;

              const otherItemKey = (
                otherItem.originalLineItemId ||
                otherItem.lineItemId ||
                otherItem._id ||
                otherItem.key
              )?.toString();

              // Skip if already added from primary assembly
              if (candidatesWithRemaining.some((c) => c.key === otherItemKey))
                return;

              const otherNetInfo = netQuantities.get(otherItemKey);
              const otherRemaining = Math.max(
                0,
                otherNetInfo?.netRemaining ??
                  otherNetInfo?.remainingQty ??
                  otherItem.netRemaining ??
                  otherItem.remainingQty ??
                  0,
              );

              if (otherRemaining > 0) {
                candidatesWithRemaining.push({
                  budgetItem: otherItem,
                  key: otherItemKey,
                  netInfo: otherNetInfo,
                  remaining: otherRemaining,
                });
              }
            });
          });

          // Now distribute the deduction across candidates
          let remainingToDeduct = Math.abs(signedDelta);

          candidatesWithRemaining.forEach((candidate) => {
            if (remainingToDeduct <= 0) return;

            const deductFromThis = Math.min(
              candidate.remaining,
              remainingToDeduct,
            );
            remainingToDeduct -= deductFromThis;

            const cBudgetItem = candidate.budgetItem;
            const cNetInfo = candidate.netInfo;
            const cKey = candidate.key;

            const cRemaining =
              cNetInfo?.netRemaining ??
              cNetInfo?.remainingQty ??
              cBudgetItem.remainingQty ??
              0;
            const cBudgeted =
              cNetInfo?.netRequiredQty ?? cBudgetItem.requiredQty ?? cRemaining;

            let cBaseCost =
              cNetInfo?.latestPrice ??
              (isMaterial
                ? cBudgetItem.price || cBudgetItem.cost
                : cBudgetItem.unitRate ||
                  cBudgetItem.cost ||
                  cBudgetItem.price) ??
              item.unitCost ??
              0;
            const cUnitCost = Number(cBaseCost.toFixed(2));

            let cCoverage =
              cNetInfo?.latestCoveragePerUnit ??
              parseFloat(cBudgetItem.coveragePerUnit) ??
              item.coveragePerUnit ??
              1;

            let cDisplaySize =
              cNetInfo?.latestSize ||
              cBudgetItem.latestSize ||
              cBudgetItem.size ||
              item.size ||
              "";

            const isCoverageItem =
              isMaterial && (isSFUnit(item.uom) || isLFUnit(item.uom));
            const baseAmount = isCoverageItem
              ? deductFromThis * cCoverage * cUnitCost
              : deductFromThis * cUnitCost;

            // Find the assembly condition name for this candidate
            let candidateConditionName =
              cBudgetItem.conditionName || item._assemblyConditionName;

            itemsToAdd.push({
              _localId: `excel-budget-${Date.now()}-${idx}`,
              itemNumber: String(idx + 1),
              sourceAssemblyId:
                cBudgetItem.assemblyId || cBudgetItem.sourceAssemblyId,
              sourceLineItemId: cKey,
              isFromBudget: true,
              laborId: item.laborId || cBudgetItem.laborId || null,
              itemCode:
                cBudgetItem.itemCode ||
                cBudgetItem.materialCode ||
                cBudgetItem.laborCode ||
                item.itemCode,
              type: isMaterial ? "material" : "labor",
              category: item.category,
              sectionNumber:
                cBudgetItem.sectionNumber || item.sectionNumber || "",
              title: item.itemDescription,
              itemDescription: item.itemDescription,
              customerDescription: item.itemDescription,
              conditionName: candidateConditionName,
              bidArea: item.bidArea || cBudgetItem.bidArea || "",
              deltaQty: -deductFromThis,
              quantity: deductFromThis,
              unitCost: cUnitCost,
              unitRate: isMaterial ? 0 : cBaseCost,
              amount: -baseAmount,
              size: cDisplaySize,
              uom: item.uom || cBudgetItem.uom || "",
              coveragePerUnit: String(cCoverage),
              vendor: cBudgetItem.vendor?._id || cBudgetItem.vendor || null,
              remainingQty: cRemaining,
              budgetedQty: cBudgeted,
              forBudget: true,
              referenceNumber: null,
              hasPriceChange: cNetInfo?.hasPriceChange || false,
              originalPrice: cNetInfo?.originalPrice ?? cUnitCost,
              isSizeChangeCloseout: cBudgetItem.isSizeChangeCloseout,
              isNewSpec: cBudgetItem.isNewSpec,
              isHistorical: cBudgetItem.isHistorical,
              rowTypeLabel: cBudgetItem.rowTypeLabel,
            });
            idx++;
          });

          return; // Done with this negative item
        }

        // ── For POSITIVE deltas, same as before — single line item ──
        const itemKey = item._sourceLineItemId;
        const netInfo = netQuantities.get(itemKey);

        const remaining =
          netInfo?.netRemaining ??
          netInfo?.remainingQty ??
          budgetItem.remainingQty ??
          0;
        const budgeted =
          netInfo?.netRequiredQty ?? budgetItem.requiredQty ?? remaining;

        let baseCost =
          netInfo?.latestPrice ??
          (isMaterial
            ? budgetItem.price || budgetItem.cost
            : budgetItem.unitRate || budgetItem.cost || budgetItem.price) ??
          item.unitCost ??
          0;
        const unitCost = Number(baseCost.toFixed(2));

        let coverage =
          netInfo?.latestCoveragePerUnit ??
          parseFloat(budgetItem.coveragePerUnit) ??
          item.coveragePerUnit ??
          1;

        let displaySize =
          netInfo?.latestSize ||
          budgetItem.latestSize ||
          budgetItem.size ||
          item.size ||
          "";

        const qty = Math.abs(signedDelta);
        const isCoverageItem =
          isMaterial && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const baseAmount = isCoverageItem
          ? qty * coverage * unitCost
          : qty * unitCost;

        itemsToAdd.push({
          _localId: `excel-budget-${Date.now()}-${idx}`,
          itemNumber: String(idx + 1),
          sourceAssemblyId:
            budgetItem.assemblyId || budgetItem.sourceAssemblyId,
          sourceLineItemId: itemKey,
          isFromBudget: true,
          laborId: item.laborId || budgetItem.laborId || null,
          itemCode:
            budgetItem.itemCode ||
            budgetItem.materialCode ||
            budgetItem.laborCode ||
            item.itemCode,
          type: isMaterial ? "material" : "labor",
          category: item.category,
          sectionNumber: budgetItem.sectionNumber || item.sectionNumber || "",
          title: item.itemDescription,
          itemDescription: item.itemDescription,
          customerDescription: item.itemDescription,
          conditionName: item._assemblyConditionName,
          bidArea: item.bidArea || budgetItem.bidArea || "",
          deltaQty: signedDelta,
          quantity: qty,
          unitCost,
          unitRate: isMaterial ? 0 : baseCost,
          amount: baseAmount,
          size: displaySize,
          uom: item.uom || budgetItem.uom || "",
          coveragePerUnit: String(coverage),
          vendor: budgetItem.vendor?._id || budgetItem.vendor || null,
          remainingQty: remaining,
          budgetedQty: budgeted,
          forBudget: true,
          referenceNumber: null,
          hasPriceChange: netInfo?.hasPriceChange || false,
          originalPrice: netInfo?.originalPrice ?? unitCost,
          isSizeChangeCloseout: budgetItem.isSizeChangeCloseout,
          isNewSpec: budgetItem.isNewSpec,
          isHistorical: budgetItem.isHistorical,
          rowTypeLabel: budgetItem.rowTypeLabel,
        });
        idx++;
      });

    // ── Database-matched items (unchanged) ──
    // ── Database-matched items ──
    validationResults.databaseMatched
      .filter((_, i) => selectedDbItems.has(i))
      .forEach((item) => {
        const dbItem = item._dbItem;
        const vendorMatch = item._vendorMatch;
        const isMaterial = item.category === "M";

        let unitCost = item.unitCost;
        let resolvedVendorId = vendorMatch?._id || null;

        if (vendorMatch && dbItem?.vendorPrices) {
          const vp = dbItem.vendorPrices.find(
            (p) => p.vendor?.toString() === vendorMatch._id?.toString(),
          );
          if (vp) unitCost = isMaterial ? vp.price : vp.unitRate || vp.price;
        } else if (dbItem) {
          unitCost = isMaterial
            ? dbItem.defaultCost || item.unitCost
            : dbItem.defaultUnitRate || item.unitCost;

          if (!resolvedVendorId) {
            resolvedVendorId =
              dbItem.defaultVendor?._id?.toString() ||
              dbItem.defaultVendor?.toString() ||
              null;
          }
        }

        const coverage = item.coveragePerUnit || 1;
        const quantity = Math.abs(item.quantity);
        const isCoverageItem =
          isMaterial && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const amount = isCoverageItem
          ? Number((quantity * coverage * unitCost).toFixed(2))
          : Number((quantity * unitCost).toFixed(2));

        itemsToAdd.push({
          _localId: `excel-db-${Date.now()}-${idx}`,
          itemNumber: String(idx + 1),
          itemDatabaseId: dbItem?._id || null,
          isFromDatabase: !!dbItem,
          itemCode: item.itemCode,
          type: isMaterial ? "material" : "labor",
          category: item.category,
          sectionNumber: item.sectionNumber || dbItem?.sectionNumber || "",
          title: item.itemDescription,
          itemDescription: item.itemDescription,
          customerDescription: item.itemDescription,
          conditionName: item.conditionName,
          bidArea: item.bidArea || "",
          laborId: item.laborId || null,
          deltaQty: quantity,
          quantity,
          unitCost: Number(unitCost.toFixed(2)),
          unitRate: isMaterial ? 0 : Number(unitCost.toFixed(2)),
          amount,
          size: item.size || dbItem?.size || "",
          uom: item.uom || dbItem?.uom || "EA",
          coveragePerUnit: String(coverage),
          vendor: resolvedVendorId,
          forBudget: true,
          referenceNumber: null,
        });
        idx++;
      });

    onAddItems(itemsToAdd);
    handleClose();
  };

  const handleClose = () => {
    setFile(null);
    setParsedItems([]);
    setValidationResults({
      budgetMatched: [],
      databaseMatched: [],
      invalid: [],
    });
    setStep(1);
    setSelectedBudgetItems(new Set());
    setSelectedDbItems(new Set());
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const totalSelected = selectedBudgetItems.size + selectedDbItems.size;

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/80 transition-opacity" />
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all w-full max-w-7xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <DialogTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <DocumentArrowUpIcon className="h-6 w-6 text-orange-500" />
                Upload Change Order from Excel
              </DialogTitle>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              {/* Step 1: Upload */}
              {step === 1 && (
                <div className="space-y-4">
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      file
                        ? "border-green-500 bg-green-500/10"
                        : "border-gray-600 hover:border-orange-500"
                    }`}
                  >
                    {file ? (
                      <div className="space-y-2">
                        <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
                        <p className="text-green-400 font-medium">
                          {file.name}
                        </p>
                        <p className="text-sm text-gray-400">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                        <button
                          onClick={() => {
                            setFile(null);
                            if (fileInputRef.current)
                              fileInputRef.current.value = "";
                          }}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <ArrowUpTrayIcon className="h-12 w-12 text-gray-500 mx-auto" />
                        <p className="text-gray-300">
                          Drag and drop your Excel file here
                        </p>
                        <p className="text-sm text-gray-500">or</p>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
                        >
                          Browse Files
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </div>
                    )}
                  </div>

                  {/* How Matching Works */}
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                      <InformationCircleIcon className="h-4 w-4 text-blue-400" />
                      How matching works:
                    </h3>
                    <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
                      <li>
                        <strong className="text-orange-300">
                          Assembly match:
                        </strong>{" "}
                        Excel condition &quot;2 - 1st - MS.18&quot; matches
                        budget assembly &quot;58 - 1st - MS.18&quot; (leading
                        number ignored)
                      </li>
                      <li>
                        <strong className="text-orange-300">Item match:</strong>{" "}
                        Within matched assembly, finds item by material/labor
                        code + size + bid area
                      </li>
                      <li>
                        <strong className="text-orange-300">
                          Negative qty validation:
                        </strong>{" "}
                        For deductions, checks net remaining quantity is
                        sufficient
                      </li>
                      <li>
                        <strong className="text-orange-300">
                          Database fallback:
                        </strong>{" "}
                        If no assembly match, looks up item code in item
                        database
                      </li>
                    </ol>
                    <div className="mt-3 flex items-center gap-4 text-xs">
                      {isLoadingDb || netLoading ? (
                        <span className="text-orange-400">
                          ⏳ Loading data...
                        </span>
                      ) : (
                        <>
                          <span className="text-green-400">
                            ✓ {budgetItems.length} budget items loaded
                          </span>
                          <span className="text-green-400">
                            ✓ {databaseItems.length} database items loaded
                          </span>
                          <span className="text-green-400">
                            ✓ {assemblyKeyMap.size} assemblies indexed
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expected Columns */}
                  <div className="bg-gray-700/50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-300 mb-2">
                      Expected Excel Columns:
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-gray-400">
                      {[
                        "Item #",
                        "Labor ID",
                        "Category",
                        "Condition",
                        "Quantity",
                        "UoM",
                        "Unit Cost",
                        "Coverage/Unit",
                        "Req Qty",
                        "Section",
                        "Mat. Code",
                        "Labor Code",
                        "Size",
                        "Area",
                      ].map((col) => (
                        <div
                          key={col}
                          className="bg-gray-800 px-2 py-1 rounded"
                        >
                          {col}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Review */}
              {step === 2 && (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-700 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">
                        {parsedItems.length}
                      </p>
                      <p className="text-xs text-gray-400">Total Parsed</p>
                    </div>
                    <div className="bg-orange-500/20 rounded-lg p-3 text-center border border-orange-500/30">
                      <p className="text-2xl font-bold text-orange-400">
                        {validationResults.budgetMatched.length}
                      </p>
                      <p className="text-xs text-gray-400">Budget Matched</p>
                    </div>
                    <div className="bg-blue-500/20 rounded-lg p-3 text-center border border-blue-500/30">
                      <p className="text-2xl font-bold text-blue-400">
                        {validationResults.databaseMatched.length}
                      </p>
                      <p className="text-xs text-gray-400">Database Matched</p>
                    </div>
                    <div className="bg-red-500/20 rounded-lg p-3 text-center border border-red-500/30">
                      <p className="text-2xl font-bold text-red-400">
                        {validationResults.invalid.length}
                      </p>
                      <p className="text-xs text-gray-400">Errors</p>
                    </div>
                  </div>

                  {/* ── Budget Matched Items ── */}
                  {validationResults.budgetMatched.length > 0 && (
                    <div className="bg-gray-700/50 rounded-lg border border-orange-500/30">
                      <div className="flex items-center justify-between p-3 border-b border-gray-600">
                        <h4 className="text-orange-400 font-medium flex items-center gap-2">
                          <CheckCircleIcon className="h-5 w-5" />
                          Budget Matched ({selectedBudgetItems.size}/
                          {validationResults.budgetMatched.length} selected)
                        </h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              setSelectedBudgetItems(
                                new Set(
                                  validationResults.budgetMatched.map(
                                    (_, i) => i,
                                  ),
                                ),
                              )
                            }
                            className="px-2 py-1 text-xs text-orange-400 hover:text-orange-300"
                          >
                            Select All
                          </button>
                          <button
                            onClick={() => setSelectedBudgetItems(new Set())}
                            className="px-2 py-1 text-xs text-gray-400 hover:text-gray-300"
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-800 sticky top-0">
                            <tr>
                              <th className="px-2 py-2 text-center w-8">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedBudgetItems.size ===
                                    validationResults.budgetMatched.length
                                  }
                                  onChange={() => {
                                    if (
                                      selectedBudgetItems.size ===
                                      validationResults.budgetMatched.length
                                    )
                                      setSelectedBudgetItems(new Set());
                                    else
                                      setSelectedBudgetItems(
                                        new Set(
                                          validationResults.budgetMatched.map(
                                            (_, i) => i,
                                          ),
                                        ),
                                      );
                                  }}
                                  className="rounded border-gray-500"
                                />
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Item Code
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Description
                              </th>
                              <th className="px-2 py-2 text-center text-gray-400">
                                Cat
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Matched Assembly
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Bid Area
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Size
                              </th>
                              <th className="px-2 py-2 text-right text-gray-400">
                                Qty Δ
                              </th>
                              <th className="px-2 py-2 text-right text-gray-400">
                                Net Remaining
                              </th>
                              <th className="px-2 py-2 text-right text-gray-400">
                                Unit Cost
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Warnings
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationResults.budgetMatched.map((item, i) => (
                              <tr
                                key={i}
                                className={`border-t border-gray-700 cursor-pointer hover:bg-gray-600/50 ${
                                  selectedBudgetItems.has(i)
                                    ? "bg-orange-500/10"
                                    : ""
                                }`}
                                onClick={() =>
                                  setSelectedBudgetItems((prev) => {
                                    const s = new Set(prev);
                                    s.has(i) ? s.delete(i) : s.add(i);
                                    return s;
                                  })
                                }
                              >
                                <td className="px-2 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={selectedBudgetItems.has(i)}
                                    onChange={() => {}}
                                    onClick={(e) => e.stopPropagation()}
                                    className="rounded border-gray-500"
                                  />
                                </td>
                                <td className="px-2 py-2 text-gray-300 font-mono">
                                  {item.itemCode}
                                </td>
                                <td className="px-2 py-2 text-gray-300 truncate max-w-[180px]">
                                  {item.itemDescription}
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <span
                                    className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                                      item.category === "M"
                                        ? "bg-blue-500/20 text-blue-300"
                                        : "bg-purple-500/20 text-purple-300"
                                    }`}
                                  >
                                    {item.category}
                                  </span>
                                </td>
                                <td className="px-2 py-2 text-orange-300 truncate max-w-[160px]">
                                  {item._assemblyConditionName}
                                </td>
                                <td className="px-2 py-2 text-gray-300">
                                  {item.bidArea}
                                </td>
                                <td className="px-2 py-2 text-gray-300 font-mono">
                                  {item.size || "—"}
                                </td>
                                <td
                                  className={`px-2 py-2 text-right font-medium ${
                                    item.quantity < 0
                                      ? "text-red-400"
                                      : "text-green-400"
                                  }`}
                                >
                                  {item.quantity > 0 ? "+" : ""}
                                  {item.quantity}
                                </td>
                                <td className="px-2 py-2 text-right text-gray-400">
                                  {item._netRemaining != null
                                    ? Number(item._netRemaining).toFixed(2)
                                    : "—"}
                                </td>
                                <td className="px-2 py-2 text-right text-gray-300">
                                  {formatCurrency(
                                    item._budgetItem
                                      ? item._budgetItem.latestPrice ||
                                          item._budgetItem.price ||
                                          item._budgetItem.cost ||
                                          item.unitCost
                                      : item.unitCost,
                                  )}
                                </td>
                                <td className="px-2 py-2 text-yellow-400 truncate max-w-[150px]">
                                  {item._warnings.length > 0
                                    ? item._warnings[0]
                                    : ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Database Matched Items ── */}
                  {validationResults.databaseMatched.length > 0 && (
                    <div className="bg-gray-700/50 rounded-lg border border-blue-500/30">
                      <div className="flex items-center justify-between p-3 border-b border-gray-600">
                        <h4 className="text-blue-400 font-medium flex items-center gap-2">
                          <CheckCircleIcon className="h-5 w-5" />
                          Database Matched ({selectedDbItems.size}/
                          {validationResults.databaseMatched.length} selected)
                        </h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() =>
                              setSelectedDbItems(
                                new Set(
                                  validationResults.databaseMatched.map(
                                    (_, i) => i,
                                  ),
                                ),
                              )
                            }
                            className="px-2 py-1 text-xs text-blue-400 hover:text-blue-300"
                          >
                            Select All
                          </button>
                          <button
                            onClick={() => setSelectedDbItems(new Set())}
                            className="px-2 py-1 text-xs text-gray-400 hover:text-gray-300"
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>
                      <div className="max-h-48 overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-800 sticky top-0">
                            <tr>
                              <th className="px-2 py-2 text-center w-8">
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedDbItems.size ===
                                    validationResults.databaseMatched.length
                                  }
                                  onChange={() => {
                                    if (
                                      selectedDbItems.size ===
                                      validationResults.databaseMatched.length
                                    )
                                      setSelectedDbItems(new Set());
                                    else
                                      setSelectedDbItems(
                                        new Set(
                                          validationResults.databaseMatched.map(
                                            (_, i) => i,
                                          ),
                                        ),
                                      );
                                  }}
                                  className="rounded border-gray-500"
                                />
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Item Code
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Description
                              </th>
                              <th className="px-2 py-2 text-center text-gray-400">
                                Cat
                              </th>
                              <th className="px-2 py-2 text-left text-gray-400">
                                Condition
                              </th>
                              <th className="px-2 py-2 text-right text-gray-400">
                                Qty
                              </th>
                              <th className="px-2 py-2 text-center text-gray-400">
                                UoM
                              </th>
                              <th className="px-2 py-2 text-right text-gray-400">
                                Unit Cost
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationResults.databaseMatched.map(
                              (item, i) => (
                                <tr
                                  key={i}
                                  className={`border-t border-gray-700 cursor-pointer hover:bg-gray-600/50 ${
                                    selectedDbItems.has(i)
                                      ? "bg-blue-500/10"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setSelectedDbItems((prev) => {
                                      const s = new Set(prev);
                                      s.has(i) ? s.delete(i) : s.add(i);
                                      return s;
                                    })
                                  }
                                >
                                  <td className="px-2 py-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedDbItems.has(i)}
                                      onChange={() => {}}
                                      onClick={(e) => e.stopPropagation()}
                                      className="rounded border-gray-500"
                                    />
                                  </td>
                                  <td className="px-2 py-2 text-gray-300 font-mono">
                                    {item.itemCode}
                                  </td>
                                  <td className="px-2 py-2 text-gray-300 truncate max-w-[200px]">
                                    {item.itemDescription}
                                    {item._notInDatabase && (
                                      <span className="ml-1 text-[10px] text-yellow-400">
                                        (not in DB)
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <span
                                      className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                                        item.category === "M"
                                          ? "bg-blue-500/20 text-blue-300"
                                          : "bg-purple-500/20 text-purple-300"
                                      }`}
                                    >
                                      {item.category}
                                    </span>
                                  </td>
                                  <td className="px-2 py-2 text-gray-300 truncate max-w-[150px]">
                                    {item.conditionName}
                                  </td>
                                  <td className="px-2 py-2 text-right text-gray-300">
                                    {item.quantity}
                                  </td>
                                  <td className="px-2 py-2 text-center text-gray-400">
                                    {item.uom}
                                  </td>
                                  <td className="px-2 py-2 text-right text-gray-300">
                                    {formatCurrency(item.unitCost)}
                                  </td>
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Invalid Items ── */}
                  {validationResults.invalid.length > 0 && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-red-400 font-medium">
                            Items with Errors (
                            {validationResults.invalid.length})
                          </h4>
                          <p className="text-sm text-gray-400 mt-1">
                            Fix these issues in the Excel file and re-upload, or
                            add missing items to your budget/database first.
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 max-h-48 overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-800 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left text-gray-400">
                                Row
                              </th>
                              <th className="px-2 py-1 text-left text-gray-400">
                                Item Code
                              </th>
                              <th className="px-2 py-1 text-left text-gray-400">
                                Condition
                              </th>
                              <th className="px-2 py-1 text-right text-gray-400">
                                Qty
                              </th>
                              <th className="px-2 py-1 text-left text-gray-400">
                                Errors
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {validationResults.invalid.map((item, idx) => (
                              <tr
                                key={idx}
                                className="border-t border-gray-700"
                              >
                                <td className="px-2 py-1 text-gray-300">
                                  {item._rowIndex}
                                </td>
                                <td className="px-2 py-1 text-gray-300 font-mono">
                                  {item.itemCode || "—"}
                                </td>
                                <td className="px-2 py-1 text-gray-300">
                                  {item.conditionName || "—"}
                                </td>
                                <td
                                  className={`px-2 py-1 text-right ${item.quantity < 0 ? "text-red-400" : "text-gray-300"}`}
                                >
                                  {item.quantity}
                                </td>
                                <td className="px-2 py-1 text-red-400">
                                  {item._errors.map((err, i) => (
                                    <div key={i}>• {err}</div>
                                  ))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-gray-700 bg-gray-800">
              <div className="text-sm text-gray-400">
                {step === 2 && totalSelected > 0 && (
                  <span className="text-green-400">
                    {totalSelected} item{totalSelected !== 1 ? "s" : ""}{" "}
                    selected to add
                    {selectedBudgetItems.size > 0 && (
                      <span className="text-orange-400 ml-2">
                        ({selectedBudgetItems.size} from budget)
                      </span>
                    )}
                    {selectedDbItems.size > 0 && (
                      <span className="text-blue-400 ml-2">
                        ({selectedDbItems.size} from database)
                      </span>
                    )}
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                {step === 1 && (
                  <>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={parseExcelFile}
                      disabled={
                        !file || isProcessing || isLoadingDb || netLoading
                      }
                      className={`px-4 py-2 text-sm text-white rounded-md flex items-center gap-2 ${
                        !file || isProcessing || isLoadingDb || netLoading
                          ? "bg-gray-600 cursor-not-allowed"
                          : "bg-orange-500 hover:bg-orange-600"
                      }`}
                    >
                      {isProcessing ? (
                        <>
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          Processing...
                        </>
                      ) : isLoadingDb || netLoading ? (
                        <>
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          Loading Data...
                        </>
                      ) : (
                        <>
                          <ArrowUpTrayIcon className="h-4 w-4" />
                          Parse & Match
                        </>
                      )}
                    </button>
                  </>
                )}
                {step === 2 && (
                  <>
                    <button
                      onClick={() => setStep(1)}
                      className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-md hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddSelectedItems}
                      disabled={totalSelected === 0}
                      className={`px-4 py-2 text-sm text-white rounded-md flex items-center gap-2 ${
                        totalSelected === 0
                          ? "bg-gray-600 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      Add {totalSelected} Item
                      {totalSelected !== 1 ? "s" : ""} to Change Order
                    </button>
                  </>
                )}
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
